/**
 * Monte Carlo Equity Curve Simulation Utility
 *
 * Generates multiple equity curve scenarios based on trading statistics
 */

/**
 * Run Monte Carlo simulation
 * @param {Object} params - Simulation parameters
 * @param {number} params.startingEquity - Starting capital
 * @param {number} params.winPercentage - Win rate (0-100)
 * @param {number} params.avgWinnerLoserRatio - Average winner/loser ratio
 * @param {number} params.avgTradesPerMonth - Average trades per month
 * @param {number} params.riskPerTrade - Risk per trade (absolute value)
 * @param {number} scenarios - Number of scenarios to generate (default: 20)
 * @param {number} months - Number of months to simulate (default: 12)
 * @returns {Array} Array of scenario equity curves
 */
export const runMonteCarloSimulation = (params, scenarios = 20, months = 12) => {
    const results = [];

    for (let s = 0; s < scenarios; s += 1) {
        let equity = params.startingEquity;
        const scenario = [{ month: 0, equity }];

        const tradesPerMonth = params.avgTradesPerMonth;
        const totalTrades = Math.floor(tradesPerMonth * months);

        for (let i = 0; i < totalTrades; i += 1) {
            const isWin = Math.random() < params.winPercentage / 100;

            if (isWin) {
                // Win: add risk * ratio
                equity += params.riskPerTrade * params.avgWinnerLoserRatio;
            } else {
                // Loss: subtract risk
                equity -= params.riskPerTrade;
            }

            // Record equity at month intervals
            const currentMonth = Math.floor((i + 1) / tradesPerMonth);
            const isMonthEnd = (i + 1) % Math.ceil(tradesPerMonth) === 0;

            if (isMonthEnd && currentMonth <= months) {
                scenario.push({ month: currentMonth, equity });
            }
        }

        // Ensure we have final month if not already added
        if (scenario[scenario.length - 1].month < months) {
            scenario.push({ month: months, equity });
        }

        results.push(scenario);
    }

    return results;
};

/**
 * Calculate median scenario from all scenarios
 * @param {Array} scenarios - Array of equity curve scenarios
 * @returns {Object} Median scenario and its index
 */
export const calculateMedianScenario = (scenarios) => {
    if (!scenarios || scenarios.length === 0) return { scenario: null, index: -1 };

    const finalEquities = scenarios.map((s) => s[s.length - 1].equity);
    const sorted = [...finalEquities].sort((a, b) => a - b);
    const medianValue = sorted[Math.floor(sorted.length / 2)];

    const medianIndex = scenarios.findIndex((s) => s[s.length - 1].equity === medianValue);

    return {
        scenario: scenarios[medianIndex],
        index: medianIndex
    };
};

/**
 * Determine color for median line based on slope
 * @param {Array} scenario - Equity curve scenario
 * @returns {string} Color code ('green', 'red', or 'black')
 */
export const getMedianLineColor = (scenario) => {
    if (!scenario || scenario.length < 2) return 'black';

    const start = scenario[0].equity;
    const end = scenario[scenario.length - 1].equity;
    const slope = end - start;

    const threshold = start * 0.01; // 1% threshold for "flat"

    if (slope > threshold) return '#29a373'; // green
    if (slope < -threshold) return '#E57373'; // red
    return '#000000'; // black
};

/**
 * Calculate statistics for all scenarios
 * @param {Array} scenarios - Array of equity curve scenarios
 * @returns {Object} Statistics including min, max, median final equity
 */
export const calculateScenarioStats = (scenarios) => {
    if (!scenarios || scenarios.length === 0) {
        return {
            minFinalEquity: 0,
            maxFinalEquity: 0,
            medianFinalEquity: 0,
            avgFinalEquity: 0
        };
    }

    const finalEquities = scenarios.map((s) => s[s.length - 1].equity);
    const sorted = [...finalEquities].sort((a, b) => a - b);

    return {
        minFinalEquity: sorted[0],
        maxFinalEquity: sorted[sorted.length - 1],
        medianFinalEquity: sorted[Math.floor(sorted.length / 2)],
        avgFinalEquity: finalEquities.reduce((sum, val) => sum + val, 0) / finalEquities.length
    };
};
